import { useEffect, useState, useMemo, useCallback, FC } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Grid, Icon, Input } from "semantic-ui-react";
import { fetchApps, fetchCategories } from "./reducer/actions";
import MarketplaceAppCard from "./bits/MarketplaceAppCard";
import { MarketplaceAppItem } from "./types";
import { PageToolbar, PageToolbarButtonsPane } from "../components/PageToolbar";
import Breadcrumbs from "../components/shared/breadcrumbs/Breadcrumbs";
import CategoryButtons from "./bits/CategoryButtons";
import { State } from "../selectors/state";
import { findChild } from "../components/sidebar/bits/helper";
import { appPages } from "../shared-constants/app-pages";
import { push } from "connected-react-router";
import { toast } from "react-toastify";

const MarketplaceAppList: FC = () => {
  const dispatch = useDispatch();

  const apps = useSelector((state: State) => state.marketplace.apps);
  const categories = useSelector(
    (state: State) => state.marketplace.categories,
  );

  const [searchQuery, setSearchQuery] = useState<string>("");
  const [selectedCategoryId, setSelectedCategoryId] = useState<number | null>(
    null,
  );

  const redirectToProvisionedApps = useCallback(() => {
    const page = findChild(appPages, "Provisioned Apps");
    if (page?.link) {
      dispatch(push(page.link));
    } else {
      toast.error("Unable to redirect");
    }
  }, [dispatch]);

  useEffect(() => {
    if (categories.length === 0) {
      dispatch(fetchCategories());
    }
    if (apps.length === 0) {
      dispatch(fetchApps());
    }
  }, [dispatch, categories.length, apps.length]);

  const breadcrumbs = useMemo(
    () => [
      { title: "Marketplace", pathname: "/marketplace" },
      { title: "Discover Apps" },
    ],
    [],
  );

  const filteredApps = useMemo(() => {
    return apps.filter((app: MarketplaceAppItem) => {
      const matchesCategory = selectedCategoryId
        ? app.categoryId === selectedCategoryId
        : true;
      const matchesSearch = app.title.toLowerCase().includes(searchQuery);
      return matchesCategory && matchesSearch;
    });
  }, [apps, selectedCategoryId, searchQuery]);

  const handleCategoryClick = useCallback((categoryId: number | null) => {
    setSelectedCategoryId(categoryId);
  }, []);

  const handleSearchChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      setSearchQuery(e.target.value.toLowerCase());
    },
    [],
  );

  return (
    <>
      <PageToolbar className={"justify-between"}>
        <Breadcrumbs breadcrumbs={breadcrumbs} />
        <PageToolbarButtonsPane>
          <button
            className="button button--transparent"
            onClick={redirectToProvisionedApps}
          >
            See Provisioned apps
            <Icon name="external square alternate" />
          </button>
        </PageToolbarButtonsPane>
      </PageToolbar>
      <div className="page-content">
        <Grid>
          <Grid.Row className="flex align-items-center">
            <Grid.Column width={12}>
              <CategoryButtons
                categories={categories}
                selectedCategoryId={selectedCategoryId}
                onCategoryClick={handleCategoryClick}
              />
            </Grid.Column>
            <Grid.Column
              className="flex justify-content-end align-items-center"
              width={4}
            >
              <div className="ui input input-white float-right">
                <Input
                  icon="search"
                  minLength={2}
                  placeholder="Enter filter text..."
                  value={searchQuery}
                  onChange={handleSearchChange}
                />
              </div>
            </Grid.Column>
          </Grid.Row>
        </Grid>

        <div className="marketplace-app-list">
          {filteredApps.length === 0 && (
            <p className="padding-left-00">
              <Icon name="exclamation circle" className="padding-right-20" />
              No app found for the selected category!
            </p>
          )}
          {filteredApps.map((app: MarketplaceAppItem) => (
            <>
              <MarketplaceAppCard key={app.id} app={app} />
            </>
          ))}
        </div>
      </div>
    </>
  );
};

export default MarketplaceAppList;
